import React, { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Box } from '@mui/material'
import { generatePath, useNavigate } from 'react-router-dom'

import { B2CPaymentsRequest } from '@/@types/b2c.types.ts'
import { TableComponent } from '@/components/table'
import { useGetB2CPaymentsList } from '@/services'
import { useIsMobile } from '@/hooks'
import { APP_ROUTES } from '@/constants.ts'
import { FetchErrorMessage } from '@/components'

import { defaultSettings, getColumnsSettings, KEY_PAGE, PartialPaymentsRequest } from '../default-settings-payments.tsx'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'
import { DefaultFiltersState } from '@/stores'
import { b2cOneTimePaymentsActions } from '@/stores/slices/b2c-slice/b2c-one-time-payments-slice.ts'
import { useB2COneTimePaymentsTableState } from '@/stores/slices/b2c-slice'

const columnHelper = createColumnHelper<B2CPaymentsRequest>()
const defaultColumns = getColumnsSettings(columnHelper)

export const B2CRequestsPaymentsTable = (): React.ReactElement => {
  const { isLoading, data, isError, refetch, isFetching } = useGetB2CPaymentsList()
  const navigate = useNavigate()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()
  const { page, showCount } = useB2COneTimePaymentsTableState()

  const b2cRequestCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialPaymentsRequest>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings]
  }, [settings, isMobile])

  const handleViewTransaction = (row: B2CPaymentsRequest) => () => {
    navigate(generatePath(APP_ROUTES.B2CView, { id: row.transferId.toString() }))
  }

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      b2cOneTimePaymentsActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<B2CPaymentsRequest>
          rowKey="transferId"
          isLoading={isLoading || isFetching}
          columns={b2cRequestCols}
          page={page}
          size={showCount}
          totalPages={data?.data.totalPages}
          data={data?.data?.content ?? []}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
          onRowClick={handleViewTransaction}
        />
      </Box>
    </FetchErrorMessage>
  )
}
